import { useMemo } from "react"
import type { BinaryOperator, Filter, Query } from "@cubejs-client/core"
import { useCubeAvailability, useCubeQuery } from "~/ui-rtk/hooks/cube"
import { VARIANTS_CUBE } from "~/ui-rtk/components/ui/drawer/ProductDetails/hooks/variants-query"

/**
 * The colorways behind each product on the page, from the variant-grain
 * feed cube, merged client-side like the Shopify sales columns.
 *
 * Why not in the mega_feed view: the variant table is provisioned per
 * tenant, and a Cube view joins unconditionally — a missing table would
 * take the whole page down instead of just this column. The cube is gated
 * by db_capabilities here, so a tenant without it simply gets no column.
 */

export interface TProductColorways {
  /** Distinct colors in the feed's order (Shopify variant ids ascend in the
   *  merchant's listed order); empty for a product without a color option. */
  colors: string[]
  /** Variants (color × size) in the feed. */
  variants: number
}

export type TColorwaysByProductId = Record<string, TProductColorways>

export default function useColorways({ productIds }: { productIds: string[] }) {
  const { isAvailable } = useCubeAvailability(VARIANTS_CUBE)

  const query = useMemo<Query>(() => {
    const filters: Filter[] = []
    if (productIds.length > 0) {
      filters.push({
        member: `${VARIANTS_CUBE}.item_group_id`,
        operator: "equals" as BinaryOperator,
        values: productIds,
      })
    }
    return {
      dimensions: [`${VARIANTS_CUBE}.item_group_id`, `${VARIANTS_CUBE}.color`],
      measures: [`${VARIANTS_CUBE}.count`],
      filters,
      // Cheap and bounded: one row per (product, color) for the page's
      // products, at most a few hundred.
      limit: 2000,
    }
  }, [productIds])

  const { resultSet, isLoading, error } = useCubeQuery(query, {
    skip: !isAvailable || productIds.length === 0,
  })

  const colorwaysByProductId = useMemo<TColorwaysByProductId>(() => {
    if (!resultSet) return {}
    // Rows come back grouped but unordered across products; keep the first
    // appearance order of each color within a product.
    const out: TColorwaysByProductId = {}
    for (const row of resultSet.rawData()) {
      const productId = String(row[`${VARIANTS_CUBE}.item_group_id`] ?? "")
      if (!productId) continue
      const color = String(row[`${VARIANTS_CUBE}.color`] ?? "").trim()
      const count = Number(row[`${VARIANTS_CUBE}.count`] ?? 0)
      const entry = out[productId] ?? { colors: [], variants: 0 }
      if (color && !entry.colors.includes(color)) entry.colors.push(color)
      entry.variants += Number.isFinite(count) ? count : 0
      out[productId] = entry
    }
    return out
  }, [resultSet])

  return { colorwaysByProductId, isAvailable, isLoading, error }
}
